import React, { useState, useEffect } from 'react';
import {
  Container,
  GridLegacy as Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Timeline as TimelineIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import api from '../services/api';
import '../utils/chartSetup'; // Initialize Chart.js

interface User {
  id: number;
  username: string;
  role: string;
  is_master?: boolean;
  sales_tracker_id?: string;
  organizationId?: number;
  isAdmin?: boolean;
  is_admin?: boolean;
}

interface ActiveUser {
  id: number;
  username: string;
  salesTrackerId: string;
}

interface WeekData {
  week?: number;
  label?: string;
  sales?: number;
  calls?: number;
  contacts?: number;
  loginTime?: number;
  utilization?: number;
  conversionRate?: string;
  contactRate?: string;
  avgSPH?: string;
  avgWinRate?: string;
  totalSales?: number;
  totalCalls?: number;
  totalContacts?: number;
  totalLoginTime?: number;
  totalUtilization?: number;
}

interface TrendsData {
  teamTrends: WeekData[];
  lastUpdated: string;
}

const DynamicTrends: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [trendsData, setTrendsData] = useState<TrendsData | null>(null);
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | 'all'>('all');
  const [aiInsights, setAiInsights] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  
  // Coaching history states
  const [coachingHistory, setCoachingHistory] = useState<any[]>([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string>('');
  const [historyLoading, setHistoryLoading] = useState(false);

  const user: User = JSON.parse(localStorage.getItem('user') || '{}');
  // Check if user has team-wide access (Superuser, Manager, or Customer permission groups)
  const hasTeamAccess = (user as any).permission_group_id === 1 || // Superuser
                        (user as any).permission_group_id === 2 || // Manager
                        (user as any).permission_group_id === 4 || // Customer
                        user.role === 'super_user' || 
                        user.role === 'admin' || 
                        user.role === 'superuser' ||
                        user.role?.toLowerCase() === 'super user' ||
                        user.isAdmin === true ||
                        user.is_admin === true;
  const isSuperUser = hasTeamAccess; // Alias for backward compatibility

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedUserId !== null) {
      fetchTrendsData();
      fetchCoachingHistory();
    }
  }, [selectedUserId]);

  const fetchCoachingHistory = async () => {
    try {
      setHistoryLoading(true);
      const userId = selectedUserId === 'all' ? undefined : selectedUserId as number;
      console.log('🔍 Fetching coaching history for userId:', userId);
      const response = await api.getCoachingHistory(userId);
      console.log('📦 API Response:', response);
      console.log('📚 Insights array:', response.insights);
      console.log('📊 Insights length:', response.insights?.length);
      setCoachingHistory(response.insights || []); // Fixed: use insights not data
      setSelectedHistoryId(''); // Reset selection
    } catch (err: any) {
      console.error('❌ Error fetching coaching history:', err);
      console.error('❌ Error details:', err.response?.data || err.message);
      // Silent fail - history is optional
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleViewSavedInsight = () => {
    if (!selectedHistoryId) return;
    
    const insight = coachingHistory.find(h => h.id.toString() === selectedHistoryId);
    if (insight) {
      setAiInsights(insight.insightsText);
      setAiError(null);
      setSaveSuccess(false);
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      // Only super users can see the dropdown and all users
      if (isSuperUser) {
        const usersResponse = await api.getActiveUsersForTrends();
        setActiveUsers(usersResponse.users || []);
      } else {
        // Non-superusers: backend will auto-filter to their own data
        // Set selectedUserId to user.id to ensure frontend knows we're in personal mode
        setSelectedUserId(user.id);
      }
      await fetchTrendsData();
    } catch (err: any) {
      console.error('Error fetching initial data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrendsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // If "all" is selected or user is not superuser, don't pass userId
      const userId = selectedUserId === 'all' ? undefined : selectedUserId as number;
      const data = await api.getDynamicTrendsData(userId);
      setTrendsData(data);
    } catch (err: any) {
      console.error('Error fetching trends data:', err);
      setError('Failed to load trends data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUserChange = (event: any) => {
    const value = event.target.value;
    setSelectedUserId(value === 'all' ? 'all' : parseInt(value));
    // Clear AI insights when changing users
    setAiInsights(null);
    setAiError(null);
  };

  const handleGetAICoachingInsights = async () => {
    try {
      setAiLoading(true);
      setAiError(null);
      setSaveSuccess(false);
      
      const userId = selectedUserId === 'all' ? undefined : selectedUserId as number;
      const response = await api.getAICoachingInsights(userId);
      
      if (response.success) {
        setAiInsights(response.insights);
      } else {
        setAiError(response.error || 'Failed to generate AI insights');
      }
    } catch (err: any) {
      console.error('Error getting AI insights:', err);
      setAiError(err.response?.data?.message || err.message || 'Failed to generate AI coaching insights');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveCoachingInsight = async () => {
    if (!aiInsights || !trendsData) return;

    try {
      setSaveLoading(true);
      setSaveSuccess(false);
      setAiError(null);

      const userId = selectedUserId === 'all' ? undefined : (selectedUserId as number);
      
      // Determine agentName: for superusers use activeUsers array, for non-superusers use logged-in user's name
      let agentName: string;
      if (selectedUserId === 'all') {
        agentName = 'Team';
      } else if (activeUsers.length > 0) {
        // Superuser with activeUsers populated
        agentName = activeUsers.find(u => u.id === userId)?.username || user.username || 'Unknown';
      } else {
        // Non-superuser - use their own username
        agentName = user.username || 'Unknown';
      }

      const weekLabels = trendsData.teamTrends.map(w => w.label || '').join(', ');

      const response = await api.saveCoachingInsight({
        userId,
        isTeamAnalysis: selectedUserId === 'all',
        agentName,
        insightsText: aiInsights,
        weekData: trendsData.teamTrends,
        analysisPeriod: weekLabels
      });

      if (response.success) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 5000); // Clear success message after 5 seconds
      } else {
        setAiError(response.error || 'Failed to save coaching insight');
      }
    } catch (err: any) {
      console.error('Error saving coaching insight:', err);
      setAiError(err.response?.data?.message || err.message || 'Failed to save coaching insight');
    } finally {
      setSaveLoading(false);
    }
  };

  /**
   * Format AI insights for user-friendly display
   * Parses markdown-style text and creates visual components
   */
  const renderFormattedInsights = (insights: string) => {
    if (!insights) return null;

    // Remove all ** markdown syntax
    const cleanText = insights.replace(/\*\*/g, '');
    
    // Split into sections
    const sections: { title: string; content: string[]; icon: string }[] = [];
    const lines = cleanText.split('\n');
    
    let currentSection: { title: string; content: string[]; icon: string } | null = null;
    
    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) return;
      
      // Detect section headers
      if (trimmed.includes('Key Findings') || trimmed.includes('KEY FINDINGS')) {
        if (currentSection) sections.push(currentSection);
        currentSection = { title: 'Key Findings', content: [], icon: '🎯' };
      } else if (trimmed.includes('Correlations') || trimmed.includes('CORRELATIONS')) {
        if (currentSection) sections.push(currentSection);
        currentSection = { title: 'Correlations', content: [], icon: '🔗' };
      } else if (trimmed.includes('Anomalies') || trimmed.includes('ANOMALIES')) {
        if (currentSection) sections.push(currentSection);
        currentSection = { title: 'Anomalies', content: [], icon: '⚠️' };
      } else if (trimmed.includes('Actionable Coaching') || trimmed.includes('ACTIONABLE COACHING') || trimmed.includes('Coaching')) {
        if (currentSection) sections.push(currentSection);
        currentSection = { title: 'Actionable Coaching', content: [], icon: '💡' };
      } else if (trimmed.includes('Trends') && !currentSection) {
        if (currentSection) sections.push(currentSection);
        currentSection = { title: 'Trends', content: [], icon: '📈' };
      } else if (currentSection) {
        // Add to current section if it's a bullet point or regular text
        if (trimmed.startsWith('-') || trimmed.startsWith('•')) {
          currentSection.content.push(trimmed.substring(1).trim());
        } else if (trimmed.length > 0 && !trimmed.endsWith(':')) {
          currentSection.content.push(trimmed);
        }
      }
    });
    
    if (currentSection) sections.push(currentSection);
    
    // If no sections detected, just show the text cleanly
    if (sections.length === 0) {
      return (
        <Box>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
            {cleanText}
          </Typography>
        </Box>
      );
    }
    
    // Render sections with nice styling
    return (
      <Box>
        {sections.map((section, idx) => (
          <Box key={idx} sx={{ mb: 3 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                pb: 1, 
                borderBottom: '2px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: '#1f2937',
                fontWeight: 600
              }}
            >
              <span style={{ fontSize: '1.5rem' }}>{section.icon}</span>
              {section.title}
            </Typography>
            <Box component="ul" sx={{ pl: 3, m: 0 }}>
              {section.content.map((item, itemIdx) => (
                <Typography 
                  key={itemIdx}
                  component="li" 
                  variant="body1"
                  sx={{ 
                    mb: 1.5, 
                    lineHeight: 1.7,
                    color: '#374151'
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderTrendsChart = () => {
    if (!trendsData?.teamTrends) return null;

    const weekLabels = trendsData.teamTrends.map(week => week.label || '');
    
    // Determine if we're showing team or individual data
    const isTeamView = selectedUserId === 'all';
    const salesData = trendsData.teamTrends.map(week => 
      isTeamView ? (week.totalSales || 0) : (week.sales || 0)
    );
    const callsData = trendsData.teamTrends.map(week => 
      isTeamView ? (week.totalCalls || 0) : (week.calls || 0)
    );
    const contactsData = trendsData.teamTrends.map(week => 
      isTeamView ? (week.totalContacts || 0) : (week.contacts || 0)
    );
    const loginTimeData = trendsData.teamTrends.map(week => {
      const minutes = isTeamView ? (week.totalLoginTime || 0) : (week.loginTime || 0);
      return Math.round(minutes / 60); // Convert to hours
    });
    const utilizationData = trendsData.teamTrends.map(week => 
      isTeamView ? (week.totalUtilization || 0) : (week.utilization || 0)
    );
    const sphData = trendsData.teamTrends.map(week => parseFloat(week.avgSPH || '0'));
    const conversionData = trendsData.teamTrends.map(week => parseFloat(week.conversionRate || '0'));

    // Calculate dynamic ranges for better sensitivity
    const calcRange = (data: number[], padding: number = 0.2) => {
      const values = data.filter(v => v > 0);
      if (values.length === 0) return { min: 0, max: 100 };
      const min = Math.min(...values);
      const max = Math.max(...values);
      const range = max - min;
      return {
        min: Math.max(0, min - (range * padding)),
        max: max + (range * padding)
      };
    };

    // Calculate sensitive ranges for ALL metrics
    const salesRange = calcRange(salesData, 0.3);
    const callsRange = calcRange(callsData, 0.2);
    const contactsRange = calcRange(contactsData, 0.2);
    const conversionRange = calcRange(conversionData, 0.3);
    const utilizationRange = calcRange(utilizationData, 0.15);
    const sphRange = calcRange(sphData, 0.3);
    
    // Normalize all metrics to 0-100 scale for visual comparison
    const normalize = (data: number[], range: {min: number, max: number}) => {
      return data.map(value => {
        if (range.max === range.min) return 50; // If no variation, return middle
        return ((value - range.min) / (range.max - range.min)) * 100;
      });
    };

    const salesNormalized = normalize(salesData, salesRange);
    const callsNormalized = normalize(callsData, callsRange);
    const contactsNormalized = normalize(contactsData, contactsRange);
    const conversionNormalized = normalize(conversionData, conversionRange);
    const utilizationNormalized = normalize(utilizationData, utilizationRange);
    const sphNormalized = normalize(sphData, sphRange);

    // NORMALIZED CHART - All metrics on 0-100 scale for visual comparison
    const multiAxisChartData = {
      labels: weekLabels,
      datasets: [
        // Volume Metrics - Normalized
        {
          label: 'Sales (Trend)',
          data: salesNormalized,
          actualData: salesData, // Store actual for tooltip
          borderColor: 'rgba(34, 197, 94, 1)', // Green
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderWidth: 3,
          tension: 0.4,
          fill: false,
          pointRadius: 6,
          pointHoverRadius: 8,
          yAxisID: 'y',
        },
        {
          label: 'Calls (Trend)',
          data: callsNormalized,
          actualData: callsData,
          borderColor: 'rgba(99, 102, 241, 1)', // Indigo
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: 'y',
          borderDash: [5, 5],
        },
        {
          label: 'Contacts (Trend)',
          data: contactsNormalized,
          actualData: contactsData,
          borderColor: 'rgba(249, 115, 22, 1)', // Orange
          backgroundColor: 'rgba(249, 115, 22, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: 'y',
          borderDash: [10, 5],
        },
        
        // Percentage Metrics - Normalized
        {
          label: 'Conversion % (Trend)',
          data: conversionNormalized,
          actualData: conversionData,
          borderColor: 'rgba(239, 68, 68, 1)', // Red
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderWidth: 3,
          tension: 0.4,
          fill: false,
          pointRadius: 6,
          pointHoverRadius: 8,
          yAxisID: 'y',
        },
        {
          label: 'Utilization % (Trend)',
          data: utilizationNormalized,
          actualData: utilizationData,
          borderColor: 'rgba(168, 85, 247, 1)', // Purple
          backgroundColor: 'rgba(168, 85, 247, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: 'y',
          borderDash: [5, 5],
        },
        
        // SPH - Normalized
        {
          label: 'SPH (Trend)',
          data: sphNormalized,
          actualData: sphData,
          borderColor: 'rgba(14, 165, 233, 1)', // Sky Blue
          backgroundColor: 'rgba(14, 165, 233, 0.1)',
          borderWidth: 3,
          tension: 0.4,
          fill: false,
          pointRadius: 6,
          pointHoverRadius: 8,
          yAxisID: 'y',
        },
      ],
    };

    const multiAxisOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              size: 12,
              weight: 'bold' as const,
            },
          },
        },
        title: {
          display: true,
          text: 'Performance Correlation Analysis - Normalized Trends (0-100 Scale)',
          font: {
            size: 16,
            weight: 'bold' as const,
          },
          padding: {
            bottom: 20,
          },
        },
        tooltip: {
          mode: 'index' as const,
          intersect: false,
          callbacks: {
            label: function(context: any) {
              const datasetIndex = context.datasetIndex;
              const dataIndex = context.dataIndex;
              const dataset = context.chart.data.datasets[datasetIndex];
              const actualValue = (dataset as any).actualData?.[dataIndex];
              
              let label = context.dataset.label || '';
              if (label) {
                label = label.replace(' (Trend)', '') + ': ';
              }
              
              // Show actual value in tooltip
              if (label.includes('Conversion %') || label.includes('Utilization %')) {
                label += actualValue?.toFixed(2) + '%';
              } else if (label.includes('SPH')) {
                label += actualValue?.toFixed(2);
              } else {
                label += Math.round(actualValue || 0).toLocaleString();
              }
              
              // Add normalized value for context
              label += ` (${context.parsed.y.toFixed(0)}% of range)`;
              
              return label;
            }
          }
        },
      },
      scales: {
        // Single Y-Axis: Normalized 0-100 scale
        y: {
          type: 'linear' as const,
          display: true,
          position: 'left' as const,
          min: 0,
          max: 100,
          title: {
            display: true,
            text: 'Normalized Trend (0=Low, 100=High)',
            font: {
              size: 13,
              weight: 'bold' as const,
            },
          },
          ticks: {
            callback: function(value: any) {
              return value + '%';
            }
          },
          grid: {
            drawOnChartArea: true,
            color: 'rgba(0, 0, 0, 0.05)',
          },
        },
      },
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
    };

    return (
      <>
        {/* Normalized Correlation Chart */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="caption" color="textSecondary" gutterBottom display="block">
            📊 <strong>Normalized Trend Analysis:</strong> All metrics scaled to 0-100 to show correlation patterns
            <br />
            💡 <strong>How to Read:</strong> 0% = Lowest value in 5-week range, 100% = Highest value. Lines moving together show correlation!
            <br />
            <br />
            <strong style={{ color: '#22c55e' }}>● Sales</strong> | 
            <strong style={{ color: '#6366f1' }}> ● Calls</strong> | 
            <strong style={{ color: '#f97316' }}> ● Contacts</strong> | 
            <strong style={{ color: '#ef4444' }}> ● Conversion %</strong> | 
            <strong style={{ color: '#a855f7' }}> ● Utilization %</strong> | 
            <strong style={{ color: '#0ea5e9' }}> ● SPH</strong>
            <br />
            <br />
            🔍 <strong>Hover over points</strong> to see actual values (e.g., "Sales: 48 (80% of range)")
          </Typography>
          <Box sx={{ height: 550, mt: 2 }}>
            <Line data={multiAxisChartData} options={multiAxisOptions} />
          </Box>
        </Paper>

        {/* Summary Cards */}
        <Grid container spacing={2}>
          {trendsData.teamTrends.map((week, index) => {
            const sales = isTeamView ? week.totalSales : week.sales;
            const calls = isTeamView ? week.totalCalls : week.calls;
            const contacts = isTeamView ? week.totalContacts : week.contacts;
            const loginTimeMinutes = isTeamView ? week.totalLoginTime : week.loginTime;
            const loginTimeHours = loginTimeMinutes ? (loginTimeMinutes / 60).toFixed(1) : '0.0';
            const utilization = isTeamView ? week.totalUtilization : week.utilization;
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="textSecondary" gutterBottom>
                      {week.label}
                    </Typography>
                    <Box mt={1}>
                      <Typography variant="h6" color="primary">
                        {sales || 0} Sales
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {calls || 0} Calls
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {contacts || 0} Contacts
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {loginTimeHours}h Login
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {utilization || 0}% Util
                      </Typography>
                      <Chip
                        label={`${week.conversionRate || 0}% Conv`}
                        size="small"
                        color="primary"
                        sx={{ mt: 1 }}
                      />
                      <Chip
                        label={`${week.avgSPH || 0} SPH`}
                        size="small"
                        color="secondary"
                        sx={{ mt: 1, ml: 0.5 }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>Loading Trends Data...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 36 }} />
          {isSuperUser ? 'Dynamic Team Coaching & Trends' : 'My Performance Coaching & Trends'}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          5-Week Performance Analysis (Monday - Saturday) • Actual Metrics Only
          {!isSuperUser && ' • Personal View'}
        </Typography>
      </Box>

      {/* User Selection (Super Users Only) */}
      {isSuperUser && activeUsers.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>View Trends For</InputLabel>
                <Select
                  value={selectedUserId}
                  onChange={handleUserChange}
                  label="View Trends For"
                >
                  <MenuItem value="all">
                    <Box display="flex" alignItems="center">
                      <PeopleIcon sx={{ mr: 1 }} />
                      All Team Members (Combined)
                    </Box>
                  </MenuItem>
                  {activeUsers.map(u => (
                    <MenuItem key={u.id} value={u.id}>
                      <Box display="flex" alignItems="center">
                        <PersonIcon sx={{ mr: 1 }} />
                        {u.username}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  icon={selectedUserId === 'all' ? <PeopleIcon /> : <PersonIcon />}
                  label={
                    selectedUserId === 'all'
                      ? 'Viewing: Team Overview'
                      : `Viewing: ${activeUsers.find(u => u.id === selectedUserId)?.username || 'Individual'}`
                  }
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label="5 Weeks of Data"
                  color="secondary"
                  variant="outlined"
                />
                <Chip
                  label="Mon-Sat Tracking"
                  color="info"
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* AI Coaching Insights */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#f0f9ff', borderLeft: '4px solid #0ea5e9' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <PsychologyIcon sx={{ mr: 1, color: '#0ea5e9', fontSize: 28 }} />
            <Typography variant="h6">AI-Powered Coaching Insights</Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={aiLoading ? <CircularProgress size={20} color="inherit" /> : <PsychologyIcon />}
            onClick={handleGetAICoachingInsights}
            disabled={aiLoading || !trendsData}
          >
            {aiLoading ? 'Analyzing...' : 'Get AI Insights'}
          </Button>
        </Box>
        
        {/* Previous Insights Dropdown */}
        {coachingHistory.length > 0 && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#0ea5e9' }}>
              📚 View Previous Coaching Sessions
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Previous Insight</InputLabel>
                  <Select
                    value={selectedHistoryId}
                    onChange={(e) => setSelectedHistoryId(e.target.value)}
                    label="Select Previous Insight"
                    disabled={historyLoading}
                  >
                    <MenuItem value="">
                      <em>-- Select a saved coaching session --</em>
                    </MenuItem>
                    {coachingHistory.map((insight) => (
                      <MenuItem key={insight.id} value={insight.id.toString()}>
                        {new Date(insight.generatedAt).toLocaleString()} - {insight.agentName}
                        {insight.analysisPeriod && ` (${insight.analysisPeriod})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={handleViewSavedInsight}
                  disabled={!selectedHistoryId}
                  startIcon={<PsychologyIcon />}
                >
                  View Selected
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
        
        {aiError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {aiError}
          </Alert>
        )}
        
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            ✅ Coaching insights saved successfully! You can view them in the coaching history.
          </Alert>
        )}

        {aiInsights && (
          <>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  📊 AI Analysis Results
                  {selectedUserId !== 'all' && ` for ${activeUsers.find(u => u.id === selectedUserId)?.username}`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                  {renderFormattedInsights(aiInsights)}
                </Box>
              </AccordionDetails>
            </Accordion>
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleSaveCoachingInsight}
                disabled={saveLoading || saveSuccess}
                startIcon={saveLoading ? <CircularProgress size={20} /> : null}
              >
                {saveLoading ? 'Saving...' : saveSuccess ? '✅ Saved' : '💾 Save Coaching Session'}
              </Button>
            </Box>
          </>
        )}
        
        {!aiInsights && !aiError && !aiLoading && (
          <Alert severity="info">
            Click "Get AI Insights" to generate data-driven coaching recommendations based on 5-week performance trends.
          </Alert>
        )}
      </Paper>

      {/* Trends Charts */}
      {renderTrendsChart()}

      {/* Info Footer */}
      <Paper sx={{ p: 2, mt: 3, bgcolor: '#f5f5f5' }}>
        <Typography variant="caption" color="textSecondary">
          📊 Data Sources: Contacts & Calls from Five9 Agent Efficiency | Sales from Sales Tracker
          <br />
          📅 Week Range: Current week + 4 weeks back (Monday through Saturday)
          <br />
          🔄 Last Updated: {trendsData?.lastUpdated ? new Date(trendsData.lastUpdated).toLocaleString() : 'N/A'}
          <br />
          💡 SPH = Sales Per Hour (based on Login Time from Five9)
        </Typography>
      </Paper>
    </Container>
  );
};

export default DynamicTrends;
